import { Tooltip } from '@lighthouse/bui'
import type { DeleteRecordActionConfig, OpenPageActionConfig, UpdateRecordActionConfig } from '@lighthouse/core'
import type {
    AliyunMessageActionConfig,
    BatchImportFileActionConfig,
    CallActionConfig,
    ClickTriggerConfig,
    ConditionConfig,
    CopyActionConfig,
    CreateRecordActionConfig,
    CreateRecordTriggerConfig,
    CreateSingleRecordActionConfig,
    DownloadFileActionConfig,
    FindRecordActionConfig,
    FindSingleRecordActionConfig,
    FlowNode,
    HandleSubProcessActionConfig,
    ImportExcelActionConfig,
    LLMActionConfig,
    LoopNodeActionConfig,
    MatchConditionTriggerConfig,
    NodeTypes,
    NotificationActionConfig,
    OpenLinkActionConfig,
    OpenWeChatCustomerServiceActionConfig,
    ParamExtractionActionConfig,
    RunCodeActionConfig,
    ScheduledTriggerConfig,
    SearchApiActionConfig,
    SendEmailActionConfig,
    SendMsgDingTalkRobotConfig,
    SendMsgWxRootConfig,
    StationMessageActionConfig,
    StripeActionConfig,
    UpdateRecordTriggerConfig,
    VariableTriggerConfig,
    WebsocketTriggerConfig,
    WeChatAddProfitSharingReceiverActionConfig,
    WeChatDeleteProfitSharingReceiverActionConfig,
    WeChatPayActionConfig,
    WeChatProfitSharingActionConfig,
    WeChatProfitSharingQueryActionConfig,
    WeChatProfitSharingQueryAmountActionConfig,
    WeChatRefundActionConfig,
    WeChatRefundQueryActionConfig,
    WechatTemplateMsgActionConfig
} from '@lighthouse/shared'
import { findNodeOption, getFlowNodeIcon, NODE_CARD_WIDTH, NodeAndEdgeStatusEnum, nodeTypeOptions } from '@lighthouse/shared'
import { useReactFlow } from '@xyflow/react'
import React, { useMemo } from 'react'

import ActionClickCard from './ActionClickCard'
import ALiYunMessageCard from './ALiYunMessageCard'
import ArgTriggerCard from './ArgTriggerCard'
import BaiduSearchApiCard from './BaiduSearchApiCard'
import BatchImportFileCard from './BatchImportFileCard'
import BingSearchApiCard from './BingSearchApiCard'
import CallCard from './CallCard'
import CodeExecuteCard from './CodeExecuteCard'
import ConditionCard from './ConditionCard'
import CopyCard from './CopyCard'
import CreateMoreRecordCard from './CreateMoreRecordCard'
import CreateRecordCard from './CreateRecordCard'
import CreateSingleRecordCard from './CreateSingleRecordCard'
import DeleteSingleRecordCard from './DeleteSingleRecordCard'
import DingTalkRobotCard from './DingTalkRobotCard'
import DownloadFileCard from './DownloadFileCard'
import EmailCard from './EmailCard'
import EndCard from './EndCard'
import FindMoreRecordCard from './FindMoreRecordCard'
import FindSingleRecordCard from './FindSingleRecordCard'
import GoogleSearchApiCard from './GoogleSearchApiCard'
import HandleSubProcessCard from './HandleSubProcessCard'
import ImportExcelCard from './ImportExcelCard'
import LLMCard from './LlmCard'
import LoopCard from './LoopCard'
import MatchConditionCard from './MatchConditionCard'
import NotificationCard from './NotificationCard'
import OpenLinkCard from './OpenLinkCard'
import OpenPageCard from './OpenPageCard'
import OpenWeChatCustomerServiceCard from './OpenWeChatCustomerServiceCard'
import ParameterExtractCard from './ParameterExtractCard'
import ScheduledCard from './ScheduledCard'
import StationMessageCard from './StationMessageCard'
import StripeCard from './StripeCard'
import * as CM from './styles'
import UpdateRecordCard from './UpdateRecordCard'
import UpdateRecordTriggerCard from './UpdateRecordTriggerCard'
import WebsocketCard from './WebsocketCard'
import WeChatAddProfitSharingReceiverCard from './WeChatAddProfitSharingReceiverCard'
import WeChatDeleteProfitSharingReceiverCard from './WeChatDeleteProfitSharingReceiverCard'
import WeChatPayCard from './WeChatPayCard'
import WeChatProfitSharingCard from './WeChatProfitSharingCard'
import WeChatProfitSharingQueryAmountCard from './WeChatProfitSharingQueryAmountCard'
import WeChatProfitSharingQueryCard from './WeChatProfitSharingQueryCard'
import WeChatRefundCard from './WeChatRefundCard'
import WeChatRefundQueryCard from './WeChatRefundQueryCard'
import WeChatTemplateMsgCard from './WeChatTemplateMsgCard'
import WxRobotCard from './WxRobotCard'

type NodeCardProps = {
    nodeType: NodeTypes
    nodeId?: string
    allParentNodes?: string[]
    errorMessage?: boolean | undefined
    data?: FlowNode['data']
    status?: NodeAndEdgeStatusEnum
}

const NodeCard: React.FC<NodeCardProps> = ({ nodeType, errorMessage, data, nodeId, status }) => {
    const { getNodes } = useReactFlow()

    const node = useMemo(() => {
        if (nodeType !== 'SEND_EMAIL_ACTION') {
            return undefined
        }
        const nodes = getNodes() as FlowNode[]
        return nodes?.find(node => node.id === nodeId)
    }, [getNodes, nodeId, nodeType])

    const nodeCard = useMemo(() => {
        if (!data?.config) {
            return null
        }

        switch (nodeType) {
            case 'CLICK_TRIGGER': {
                // 当点击时
                return <ActionClickCard config={data.config as ClickTriggerConfig} />
            }

            case 'WEBSOCKET_TRIGGER': {
                return <WebsocketCard config={data.config as WebsocketTriggerConfig} />
            }

            case 'CREATE_RECORD_TRIGGER': {
                // 当记录被创建时
                return <CreateRecordCard config={data.config as CreateRecordTriggerConfig} />
            }
            case 'UPDATE_RECORD_TRIGGER': {
                // 当记录被更新时
                return <UpdateRecordTriggerCard config={data.config as UpdateRecordTriggerConfig} />
            }
            case 'MATCH_CONDITION_TRIGGER': {
                // 当记录满足条件时
                return <MatchConditionCard config={data.config as MatchConditionTriggerConfig} />
            }
            case 'SCHEDULED_TRIGGER': {
                // 定时触发
                return <ScheduledCard config={data.config as ScheduledTriggerConfig} />
            }

            case 'ARG_TRIGGER': {
                // 新建子流程
                return <ArgTriggerCard config={data.config as VariableTriggerConfig} />
            }

            case 'HANDLE_SUB_PROCESS': {
                // 执行子流程
                return <HandleSubProcessCard config={data.config as HandleSubProcessActionConfig} />
            }

            case 'LLM_ACTION': {
                // 大语言模型 LLM
                return <LLMCard config={data.config as LLMActionConfig} />
            }
            case 'GOOGLE_SEARCH_ACTION': {
                // google search api
                return <GoogleSearchApiCard config={data.config as SearchApiActionConfig} />
            }
            case 'BING_SEARCH_ACTION': {
                // bing search api
                return <BingSearchApiCard config={data.config as SearchApiActionConfig} />
            }
            case 'BAIDU_SEARCH_ACTION': {
                // baidu search api
                return <BaiduSearchApiCard config={data.config as SearchApiActionConfig} />
            }
            case 'CODE_ACTION': {
                // 代码执行
                return <CodeExecuteCard config={data.config as RunCodeActionConfig} />
            }
            case 'PARAM_EXTRACT_ACTION': {
                // 参数提取
                return <ParameterExtractCard config={data.config as ParamExtractionActionConfig} />
            }

            case 'DINGTALK_ROBOT_ACTION': {
                // 钉钉机器人
                return <DingTalkRobotCard config={data.config as SendMsgDingTalkRobotConfig} />
            }

            case 'WX_ROBOT_ACTION': {
                // 企业微信
                return <WxRobotCard config={data.config as SendMsgWxRootConfig} />
            }

            case 'CONDITION': {
                // // 条件分支
                return <ConditionCard config={data.config as ConditionConfig} />
            }

            case 'CREATE_SINGLE_RECORD_ACTION': {
                // 创建单条记录
                return <CreateSingleRecordCard config={data.config as CreateSingleRecordActionConfig} />
            }

            case 'CREATE_RECORD_ACTION': {
                // 创建多条记录
                return <CreateMoreRecordCard config={data.config as CreateRecordActionConfig} />
            }

            case 'UPDATE_RECORD_ACTION': {
                // 更新记录
                return <UpdateRecordCard config={data.config as UpdateRecordActionConfig} />
            }

            case 'FIND_SINGLE_RECORD_ACTION': {
                // 查询单条数据
                return <FindSingleRecordCard config={data.config as FindSingleRecordActionConfig} />
            }

            case 'FIND_RECORD_ACTION': {
                // 查询多条数据
                return <FindMoreRecordCard config={data.config as FindRecordActionConfig} />
            }

            case 'DELETE_RECORD': {
                // 删除单条记录
                return <DeleteSingleRecordCard config={data.config as DeleteRecordActionConfig} />
            }
            case 'IMPORT_EXCEL': {
                // 导入 Excel 到数据表
                return <ImportExcelCard config={data.config as ImportExcelActionConfig} />
            }

            case 'BATCH_IMPORT_FILE': {
                // 批量导入文件
                return <BatchImportFileCard config={data.config as BatchImportFileActionConfig} />
            }

            case 'LOOP_ACTION': {
                // 循环
                return <LoopCard config={data.config as LoopNodeActionConfig} />
            }

            case 'STATION_MESSAGE_ACTION': {
                // 发送站内信
                return <StationMessageCard config={data.config as StationMessageActionConfig} />
            }

            case 'ALIYUN_MESSAGE_ACTION': {
                // 阿里云 发送短信
                return <ALiYunMessageCard config={data.config as AliyunMessageActionConfig} />
            }

            case 'WECHAT_TEMPLATE_MSG_ACTION': {
                // 发送模板消息
                return <WeChatTemplateMsgCard config={data.config as WechatTemplateMsgActionConfig} />
            }

            case 'OPEN_PAGE': {
                // 打开页面
                return <OpenPageCard config={data.config as OpenPageActionConfig} />
                // return null;
            }

            case 'OPEN_LINK': {
                // 打开链接
                return <OpenLinkCard config={data.config as OpenLinkActionConfig} />
            }

            case 'OPEN_WECHAT_CUSTOMER_SERVICE': {
                // 打开微信客服
                return <OpenWeChatCustomerServiceCard config={data.config as OpenWeChatCustomerServiceActionConfig} />
            }

            case 'GO_BACK': {
                // 返回上一步
                return null
            }

            case 'NOTIFICATION': {
                // 显示提示
                return <NotificationCard config={data.config as NotificationActionConfig} />
            }

            case 'COPY': {
                // 复制到剪切板
                return <CopyCard config={data.config as CopyActionConfig} />
            }

            case 'CLOSE_FLOAT': {
                // 关闭弹窗
                return null
            }

            case 'DOWNLOAD_FILE': {
                // 下载文件
                return <DownloadFileCard config={data.config as DownloadFileActionConfig} />
            }

            case 'CALL': {
                // 拨打电话
                return <CallCard config={data.config as CallActionConfig} />
            }

            case 'REFRESH_PAGE': {
                // 刷新页面数据
                return null
            }
            case 'REFRESH_USER_PERMISSIONS': {
                return null
            }

            case 'WECHAT_PAY': {
                // 微信 支付
                return <WeChatPayCard config={data.config as WeChatPayActionConfig} />
            }

            case 'WECHAT_REFUND': {
                // 微信 退款
                return <WeChatRefundCard config={data.config as WeChatRefundActionConfig} />
            }

            case 'WECHAT_REFUND_QUERY': {
                // 微信 退款查询
                return <WeChatRefundQueryCard config={data.config as WeChatRefundQueryActionConfig} />
            }

            case 'WECHAT_PROFIT_SHARING': {
                // 微信 分账
                return <WeChatProfitSharingCard config={data.config as WeChatProfitSharingActionConfig} />
            }

            case 'WECHAT_PROFIT_SHARING_QUERY': {
                // 微信 分账查询
                return <WeChatProfitSharingQueryCard config={data.config as WeChatProfitSharingQueryActionConfig} />
            }

            case 'WECHAT_PROFIT_SHARING_QUERY_AMOUNT': {
                // 微信 分账查询剩余待分金额
                return <WeChatProfitSharingQueryAmountCard config={data.config as WeChatProfitSharingQueryAmountActionConfig} />
            }

            case 'WECHAT_ADD_RECEIVER': {
                // 微信 添加分账接收方
                return <WeChatAddProfitSharingReceiverCard config={data.config as WeChatAddProfitSharingReceiverActionConfig} />
            }

            case 'WECHAT_DELETE_RECEIVER': {
                // 微信 删除分账接收方
                return <WeChatDeleteProfitSharingReceiverCard config={data.config as WeChatDeleteProfitSharingReceiverActionConfig} />
            }

            case 'SEND_EMAIL_ACTION': {
                // 企业邮箱发送邮件  腾讯 ｜ 126网易
                return <EmailCard config={data.config as SendEmailActionConfig} />
            }

            case 'STRIPE': {
                // 发起支付
                // 支付订单从数据表选择 ｜ 支付订单从上游节点选择
                return <StripeCard config={data.config as StripeActionConfig} />
            }

            case 'END': {
                // 结束
                return <EndCard />
            }

            default: {
                return null
            }
        }
    }, [nodeType, data])

    const nodeInfo = useMemo(() => {
        if (!data) {
            return null
        }
        const foundOption = findNodeOption(data, nodeTypeOptions)
        const icon = getFlowNodeIcon(data)
        return { ...foundOption, icon }
    }, [data])

    const statusIcon = useMemo(() => {
        if (errorMessage || NodeAndEdgeStatusEnum.FAILED === status) {
            return {
                icon: 'WarningCircle',
                color: `var(--color-red-500)`
            }
        } else if (NodeAndEdgeStatusEnum.SUCCESS === status) {
            return {
                icon: 'TickCircle',
                color: 'var(--color-green-500)'
            }
        } else if (NodeAndEdgeStatusEnum.RUNNING === status) {
            return {
                icon: 'Loading',
                color: 'var(--color-purple-500)',
                animation: true
            }
        }
        return null
    }, [errorMessage, status])

    return (
        <CM.Container width={NODE_CARD_WIDTH}>
            <CM.Header>
                <CM.HeaderLeft>
                    <CM.Logo background={nodeInfo?.background}>
                        <CM.Icon size={16} type={nodeInfo?.icon || 'HomeLiner'} color={nodeInfo?.color} />
                    </CM.Logo>
                    <CM.NodeName>{data?.name}</CM.NodeName>
                </CM.HeaderLeft>
                {statusIcon && (
                    <CM.HeaderRight>
                        <Tooltip title="配置未完善">
                            <CM.Icon size={16} type={statusIcon.icon} color={statusIcon?.color} animation={statusIcon.animation} />
                        </Tooltip>
                    </CM.HeaderRight>
                )}
            </CM.Header>
            <CM.Content>{nodeCard}</CM.Content>
            {data?.description && <CM.Footer>{data?.description}</CM.Footer>}
        </CM.Container>
    )
}

export default NodeCard
